import React, { useContext, useState, useRef, useEffect, useId } from 'react';
import finvuConsentRevoke from '../../assets/FinvuConsentRevoke.svg';
import personLogo from '../../assets/person.svg';
import useIsMobile from '../../hooks/useIsMobile';
import { UserContext } from '../../context/UserContext';
import { useHistory } from 'react-router-dom';
import AuthApis from '../../helper/api.service';
import { postMessage } from '../../utils/common.utils';
import { toast } from 'material-react-toastify';
import { handleEscapeKey } from '../../utils/a11y.utils';
import { maskVuaForDisplay } from '../../utils/pii.utils';

const Header = ({ customDetails }) => {
    const isMobile = useIsMobile();
    const { userId, clearConsentCache } = useContext(UserContext);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const menuId = useId();
    const menuButtonId = useId();
    const logoutButtonRef = useRef(null);
    const history = useHistory();
    const authApis = new AuthApis();
    const menuBorderColor = customDetails?.btnBorderColor || '#6B6D6F';
    const maskedUserId = userId ? maskVuaForDisplay(userId) : '';

    const fadeInStyle = `
        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: translateY(-10px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
    `;

    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = fadeInStyle;
        document.head.appendChild(styleElement);

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        const handleDocumentKeyDown = (event) => {
            handleEscapeKey(event, () => setShowDropdown(false));
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleDocumentKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleDocumentKeyDown);
            document.head.removeChild(styleElement);
        };
    }, []);

    useEffect(() => {
        if (!showDropdown) return undefined;
        const rafId = requestAnimationFrame(() => {
            logoutButtonRef.current?.focus();
        });
        return () => cancelAnimationFrame(rafId);
    }, [showDropdown]);

    const handleUserClick = () => {
        setShowDropdown(!showDropdown);
    };

    const handleLogout = () => {
        clearConsentCache();
        
        authApis.logout(userId).then((res) => {
            postMessage('window', 'CLOSED');
            if (res?.status === 'SUCCESS') {
                toast.success('Logged out successfully');
                setTimeout(() => {
                    history.push('/');
                    window.location.reload();
                }, 1000);
            } else {
                toast.error(res?.message || 'Logout failed');
            }
        }).catch(() => {
            toast.error('Logout failed');
        });
        
        setShowDropdown(false);
    };

    return (
        <nav
            className="navbar fixed-top py-2"
            aria-label="Main navigation"
            style={{ borderBottom: '1.5px solid #e0e0e0', backgroundColor: '#fff' }}
        >
            <div className="container" style={{ width: isMobile ? '100%' : '70%' }}>
                <div
                    className="d-flex align-items-center justify-content-between w-100"
                    style={{ flexWrap: 'wrap' }}
                >
                    <div className="navbar-brand d-flex align-items-center">
                        <img
                            src={finvuConsentRevoke}
                            className="finvu-logo"
                            alt="Finvu"
                            height={isMobile ? '18px' : '25px'}
                            style={{ maxHeight: isMobile ? '18px' : '25px' }}
                        />
                    </div>
                    {userId && (
                        <div style={{ position: 'relative' }} ref={dropdownRef}>
                            <button
                                type="button"
                                id={menuButtonId}
                                className="card-action-btn"
                                onClick={handleUserClick}
                                aria-expanded={showDropdown}
                                aria-haspopup="menu"
                                aria-controls={menuId}
                                aria-label={maskedUserId ? `User menu, ${maskedUserId}` : 'User menu'}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: isMobile ? '10px' : customDetails?.fontSize,
                                    fontWeight: 'bold',
                                    border: `1.5px solid ${menuBorderColor}`,
                                    borderRadius: '20px',
                                    padding: isMobile ? '2px 8px 2px 6px' : '4px 14px 4px 10px',
                                    background: showDropdown ? '#f8f9fa' : '#fff',
                                    marginTop: isMobile ? '6px' : 0,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    borderColor: showDropdown ? customDetails?.primaryColor || '#007bff' : menuBorderColor,
                                }}
                            >
                                <img src={personLogo} alt="" aria-hidden="true" style={{ height: isMobile ? '16px' : '22px', marginRight: '6px' }} />
                                <span>{maskedUserId}</span>
                            </button>
                            
                            {showDropdown && (
                                <div
                                    id={menuId}
                                    role="menu"
                                    aria-labelledby={menuButtonId}
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 4px)',
                                        right: 0,
                                        backgroundColor: '#ffffff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        boxShadow: '0 1px 6px rgba(0,0,0,0.12)',
                                        zIndex: 1000,
                                        minWidth: '80px'
                                    }}
                                >
                                    <button
                                        type="button"
                                        role="menuitem"
                                        ref={logoutButtonRef}
                                        onClick={handleLogout}
                                        className="card-action-btn w-100"
                                        style={{
                                            padding: '8px 16px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            color: '#666',
                                            fontWeight: '400',
                                            whiteSpace: 'nowrap',
                                            borderRadius: '6px',
                                        }}
                                    >
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
};

export default Header;
